import axios, { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import { defaultItemsInventoryFilters } from '../constants/itemsInventory';
import { IItemsInventoryFilters } from '../types/itemsInventory';
import { defaultPagination } from 'src/constants/main';
import { userStore } from 'src/mobx/userStore';
import client from 'src/services/client';
import { IDeal, IMeal } from 'src/types/combo';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { IProductV4, PRODUCT_TYPE } from 'src/types/product';
import { booleanNumberReverse, getCancelToken, getLastArrayItem, getTranslate } from 'src/utils/helpers';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types/index';
import { SCREENS } from '~/constants/screens';
import { history } from '~/services/history';
import logger from '~/services/logger';
import { findSubstring } from '~/utils/parser';

export class ItemsInventoryStore {
  private type: PRODUCT_TYPE;

  constructor(type: PRODUCT_TYPE) {
    this.type = type;
    makeObservable(this);
  }

  private cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable public pagination: IPagination<IProductV4 | IDeal | IMeal> = defaultPagination;
  @observable public filters: IItemsInventoryFilters = this.defaultItemsInventoryFilters;

  public get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  private get defaultItemsInventoryFilters(): IItemsInventoryFilters {
    return {
      ...defaultItemsInventoryFilters,
      branch: userStore?.user.branches?.length
        ? [
            {
              value: userStore.user.branches[0].id,
              label: getTranslate(userStore.user.branches[0].name),
              data: userStore.user.branches[0],
            },
          ]
        : [],
    };
  }

  private get storeName(): string {
    switch (this.type) {
      case PRODUCT_TYPE.DEAL_COMBO:
        return 'deals';
      case PRODUCT_TYPE.MEAL_COMBO:
        return 'meals';
      case PRODUCT_TYPE.PRODUCT:
      default:
        return 'products';
    }
  }

  private get screenPath(): string {
    switch (this.type) {
      case PRODUCT_TYPE.DEAL_COMBO:
        return SCREENS.DEAL_COMBOS();
      case PRODUCT_TYPE.MEAL_COMBO:
        return SCREENS.MEAL_COMBOS();
      case PRODUCT_TYPE.PRODUCT:
      default:
        return SCREENS.PRODUCTS();
    }
  }

  @action public fetchItems = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'fetchItems';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.loading = true;
      this.pagination.refreshing = true;

      const pagination = await client.get('/v4/products', {
        params: {
          page,
          category_id: getLastArrayItem<IPickerOption>(this.filters.category)?.value,
          name: this.filters.name,
          status: this.filters.status,
          branch_id: getLastArrayItem<IPickerOption>(this.filters.branch)?.value || userStore.user.branches[0].id,
          sort_field: this.filters.sortField,
          sort_dir: this.filters.sortDir,
          is_delete: this.filters.is_delete,
          types: [this.type],
        },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
        data: pagination.data,
      };
    } catch (error: any) {
      if (axios.isCancel(error)) return;

      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: `ItemsInventoryStore ${this.storeName} fetchItems` });
    }
  };

  @action public changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status } : item));

      await client.patch(
        `/v4/products/${id}?branch_id=${(getLastArrayItem<IPickerOption>(this.filters.branch)?.value as number) || userStore.user.branches[0].id}`,
        { status },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );
    } catch (error: any) {
      if (axios.isCancel(error)) return;

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, status: booleanNumberReverse(item.status) } : item));
      logger.error({ error, place: `ItemsInventoryStore ${this.storeName} changeStatus` });
    }
  };

  @action public changeStock = async (id: number, stock: TBooleanInNumber) => {
    try {
      const cancelId = `stock_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((item) => (item.id === id ? { ...item, out_of_stock: stock } : item));

      await client.patch(
        `/v4/products/${id}?branch_id=${(getLastArrayItem<IPickerOption>(this.filters.branch)?.value as number) || userStore.user.branches[0].id}`,
        { out_of_stock: stock },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );
    } catch (error: any) {
      if (axios.isCancel(error)) return;

      this.pagination.data = this.pagination.data.map((item) =>
        item.id === id ? { ...item, out_of_stock: booleanNumberReverse(item.out_of_stock) } : item
      );
      logger.error({ error, place: `ItemsInventoryStore ${this.storeName} changeStock` });
    }
  };

  @action public setFilters = (filters: Partial<IItemsInventoryFilters>, withoutFetch?: boolean) => {
    this.filters = { ...this.filters, ...filters };

    if (withoutFetch) return;

    this.fetchItems(1);
  };

  public handleBranchUpdateBySocket = () => {
    if (!userStore.user.branches.find((branch) => branch.id === getLastArrayItem<IPickerOption>(this.filters.branch)?.value)) {
      if (!userStore.user.branches.length) return;

      const branch: IPickerOption | undefined = {
        label: getTranslate(userStore.user.branches[0].name),
        value: userStore.user.branches[0].id,
        data: userStore.user.branches[0],
      };

      this.setFilters({ branch: [branch] }, !findSubstring(history.location.pathname, this.screenPath));
    }
  };

  @action public clear = () => {
    Object.values(this.cancelStack).forEach((token) => token('Cancelled'));
  };
}
